import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import {
  AcceptTerms,
  ForgotPassword,
  type IPrivateRouteProps as IPrivateAuthRouteProps,
  Login,
  PermissionRedirect,
  PrivateRoute as PrivateAuthRoute,
  ResetPassword,
  TermsDocument,
} from '@gbs-monorepo-packages/auth';
import { HighUserRoles, NotViewerUsers } from '@gbs-monorepo-packages/common';
import * as Sentry from '@sentry/react';

import {
  GlobalRedirect,
  GlobalRedirectRoute,
} from './components/GlobalRedirect';
import { FormDocumentLink } from './components/GrapesJS/Blocks/ButtonLink/ModalLinkSettings/components/FormDocumentLink';
import { DocumentsButtonLink } from './components/GrapesJS/Blocks/ButtonLink/ModalLinkSettings/components/FormDocumentLink/Documents';
import { Folders } from './components/GrapesJS/Blocks/ButtonLink/ModalLinkSettings/components/FormDocumentLink/Folders';
import {
  ACCEPT_TERMS,
  ALL_MEMBERS,
  ANALYTICS,
  CLIENT,
  CLIENTS,
  COURSES,
  COURSES_TEMPLATE,
  COURSE_VIEWER,
  CREATE_TEMPLATE,
  CREATE_TEMPLATE_MODAL_BUTTON_LINK_FOLDER,
  CREATE_TEMPLATE_MODAL_BUTTON_LINK_FOLDER_ITEMS,
  DASHBOARD,
  DOCUMENTS,
  EDIT_COURSE,
  EDIT_COURSE_MODAL_BUTTON_LINK_FOLDER,
  EDIT_COURSE_MODAL_BUTTON_LINK_FOLDER_ITEMS,
  FOLDER,
  FOLDERS,
  FORGOT_PASSWORD,
  HOME,
  LOGIN,
  MEMBERS,
  NOT_FOUND,
  PERMISSION_REDIRECT,
  PROFILE,
  REDIRECT,
  REDIRECT_LOGIN,
  RESET_PASSWORD,
  TEMPLATE_VIEW,
  TERMS_AND_CONDITIONS,
} from './constants/RoutePaths';
import { CoursesProvider } from './contexts/course';
import { CoursePagesProvider } from './contexts/coursePage';
import { DocumentProvider } from './contexts/documents';
import { FolderProvider } from './contexts/folder';
import { CompanyLayout } from './layouts/CompanyLayout';
import { DefaultLayout } from './layouts/DefaultLayout';
import { Analytics } from './pages/Analytics';
import { Companies } from './pages/Companies';
import { Courses } from './pages/Courses';
import { CourseViewer } from './pages/CourseViewer';
import { Dashboard } from './pages/Dashboard';
import { Documents } from './pages/Documents';
import { FoldersDocuments } from './pages/DocumentsFolders';
import { EditCourse } from './pages/EditCourse';
import { Home } from './pages/Home';
import { Members } from './pages/Members';
import { MembersCompany } from './pages/MembersCompany';
import { ProfileSettings } from './pages/ProfileSettings';
import { Redirect } from './pages/Redirect';
import { RedirectLogin } from './pages/RedirectLogin';
import { TemplateCourses } from './pages/TemplateCourses';
import { TemplatePreview } from './pages/TemplatePreview';
import { getRouteFrom } from './utils/getRoutes';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const Router = (): JSX.Element => {
  interface ILocationState {
    from: string;
  }

  const location = useLocation();
  const state = location.state as ILocationState | undefined;

  const fromDashboard = !(state?.from ?? '').includes('companies');
  return (
    <GlobalRedirectRoute>
      <SentryRoutes>
        <Route path="/" element={<DefaultLayout />}>
          <Route
            path={DASHBOARD.path}
            element={
              <PrivateRoute roles={HighUserRoles}>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path={ALL_MEMBERS.path}
            element={
              <PrivateRoute roles={HighUserRoles}>
                <Members />
              </PrivateRoute>
            }
          />

          <Route path={getRouteFrom(REDIRECT)} element={<Redirect />} />
        </Route>

        <Route
          path="/"
          element={fromDashboard ? <DefaultLayout /> : <CompanyLayout />}
        >
          <Route
            path={PROFILE.path}
            element={
              <PrivateRoute>
                <ProfileSettings />
              </PrivateRoute>
            }
          />
        </Route>
        <Route path="/" element={<CompanyLayout />}>
          <Route path={CLIENT.path + '/*'}>
            <Route
              path={COURSES.path}
              element={
                <PrivateRoute>
                  <Courses />
                </PrivateRoute>
              }
            />
            <Route
              path={ANALYTICS.path}
              element={
                <PrivateRoute>
                  <Analytics />
                </PrivateRoute>
              }
            />
            <Route element={<PrivateRoute roles={NotViewerUsers} />}>
              <Route
                path={FOLDERS.path}
                element={
                  <FolderProvider>
                    <FoldersDocuments />
                  </FolderProvider>
                }
              />
              <Route path={FOLDER.path + '/*'}>
                <Route
                  path={DOCUMENTS.path}
                  element={
                    <FolderProvider>
                      <DocumentProvider>
                        <Documents />
                      </DocumentProvider>
                    </FolderProvider>
                  }
                />
                <Route
                  path="*"
                  element={<Navigate to={DOCUMENTS.path} replace />}
                />
              </Route>
              <Route path={MEMBERS.path} element={<MembersCompany />} />
            </Route>
            <Route element={<PrivateRoute roles={HighUserRoles} />}>
              <Route
                path={COURSES_TEMPLATE.path}
                element={<TemplateCourses />}
              />
            </Route>
            <Route path="*" element={<Navigate to={COURSES.path} replace />} />
          </Route>
          <Route
            path={CLIENTS.path}
            element={
              <PrivateRoute roles={HighUserRoles}>
                <Companies />
              </PrivateRoute>
            }
          />
        </Route>

        <Route element={<PrivateRoute roles={NotViewerUsers} />}>
          <Route
            path={getRouteFrom(EDIT_COURSE)}
            element={
              <CoursesProvider>
                <CoursePagesProvider>
                  <FolderProvider>
                    <DocumentProvider>
                      <EditCourse />
                    </DocumentProvider>
                  </FolderProvider>
                </CoursePagesProvider>
              </CoursesProvider>
            }
          >
            <Route
              path={getRouteFrom(EDIT_COURSE_MODAL_BUTTON_LINK_FOLDER)}
              element={<FormDocumentLink />}
            >
              <Route index element={<Folders />} />
              <Route
                path={getRouteFrom(EDIT_COURSE_MODAL_BUTTON_LINK_FOLDER_ITEMS)}
                element={<DocumentsButtonLink />}
              />
              <Route
                path="*"
                element={
                  <Navigate
                    to={getRouteFrom(
                      EDIT_COURSE_MODAL_BUTTON_LINK_FOLDER_ITEMS
                    )}
                    replace
                  />
                }
              />
            </Route>
          </Route>
          <Route
            path={getRouteFrom(TEMPLATE_VIEW)}
            element={<TemplatePreview />}
          />
        </Route>

        <Route element={<PrivateRoute roles={HighUserRoles} />}>
          <Route
            path={getRouteFrom(CREATE_TEMPLATE)}
            element={
              <CoursesProvider>
                <CoursePagesProvider>
                  <FolderProvider>
                    <DocumentProvider>
                      <EditCourse />
                    </DocumentProvider>
                  </FolderProvider>
                </CoursePagesProvider>
              </CoursesProvider>
            }
          >
            <Route
              path={getRouteFrom(CREATE_TEMPLATE_MODAL_BUTTON_LINK_FOLDER)}
              element={<FormDocumentLink />}
            >
              <Route index element={<Folders />} />
              <Route
                path={getRouteFrom(
                  CREATE_TEMPLATE_MODAL_BUTTON_LINK_FOLDER_ITEMS
                )}
                element={<DocumentsButtonLink />}
              />
              <Route
                path="*"
                element={
                  <Navigate
                    to={getRouteFrom(
                      CREATE_TEMPLATE_MODAL_BUTTON_LINK_FOLDER_ITEMS
                    )}
                    replace
                  />
                }
              />
            </Route>
          </Route>
        </Route>
        <Route
          path={getRouteFrom(FORGOT_PASSWORD)}
          element={<ForgotPassword authorizationRoute={LOGIN} />}
        />
        <Route path={getRouteFrom(COURSE_VIEWER)} element={<CourseViewer />} />
        <Route path={getRouteFrom(LOGIN)} element={<Login />} />
        <Route
          path={getRouteFrom(REDIRECT_LOGIN)}
          element={<RedirectLogin />}
        />
        <Route
          path={getRouteFrom(RESET_PASSWORD)}
          element={<ResetPassword />}
        />
        <Route
          path={getRouteFrom(PERMISSION_REDIRECT)}
          element={<PermissionRedirect />}
        />
        <Route
          path={getRouteFrom(ACCEPT_TERMS)}
          element={<AcceptTerms />}
        />
        <Route
          path={getRouteFrom(TERMS_AND_CONDITIONS)}
          element={<TermsDocument />}
        />

        <Route path="/">
          <Route path={HOME.path} element={<Home />} />
        </Route>

        <Route path="*" element={<GlobalRedirect />} />
      </SentryRoutes>
    </GlobalRedirectRoute>
  );
};

type IPrivateRouteProps = Partial<IPrivateAuthRouteProps>;

const PrivateRoute = ({
  children,
  loginPath = LOGIN,
  permissions,
  redirectPath = NOT_FOUND,
  roles,
}: IPrivateRouteProps): JSX.Element => {
  return (
    <PrivateAuthRoute
      loginPath={loginPath}
      permissions={permissions}
      redirectPath={redirectPath}
      roles={roles}
    >
      {children}
    </PrivateAuthRoute>
  );
};
